import { useEffect, useState } from 'react';
import api from '../utils/api';

/** @type {Map<string, { status: 'loading'|'ok'|'error', data?: object }>} */
const previewCache = new Map();
/** @type {Map<string, Promise<object|null>>} */
const inflight = new Map();

async function fetchPreview(url) {
  if (!url) return null;
  const cached = previewCache.get(url);
  if (cached?.status === 'ok') return cached.data;
  if (cached?.status === 'error') return null;

  if (inflight.has(url)) {
    return inflight.get(url);
  }

  previewCache.set(url, { status: 'loading' });
  const promise = api
    .get('/crm/chat/link-preview', { params: { url } })
    .then((res) => {
      const data = res.data?.data ?? null;
      if (data && (data.title || data.image || data.description || data.site_name)) {
        previewCache.set(url, { status: 'ok', data });
        return data;
      }
      previewCache.set(url, { status: 'error' });
      return null;
    })
    .catch(() => {
      previewCache.set(url, { status: 'error' });
      return null;
    })
    .finally(() => {
      inflight.delete(url);
    });

  inflight.set(url, promise);
  return promise;
}

export default function useLinkPreview(url) {
  const [preview, setPreview] = useState(() => {
    if (!url) return null;
    const cached = previewCache.get(url);
    return cached?.status === 'ok' ? cached.data : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!url) return false;
    const cached = previewCache.get(url);
    return !cached || cached.status === 'loading';
  });

  useEffect(() => {
    if (!url) {
      setPreview(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const cached = previewCache.get(url);
    if (cached?.status === 'ok') {
      setPreview(cached.data);
      setLoading(false);
      return undefined;
    }
    if (cached?.status === 'error') {
      setPreview(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    fetchPreview(url).then((data) => {
      if (cancelled) return;
      setPreview(data);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { preview, loading };
}
